import * as THREE from 'three'

/**
 * Palette utilities for the Video Lab.
 *
 * The recoloring is done with a 256×1 RGBA LUT (look-up table): for each gray
 * level 0..255 we precompute the output color on the CPU. The shader only has to
 * sample the LUT with the video's luminance. Building the banding here (and not
 * in GLSL) keeps the shader trivial and gives us full control of the mapping.
 */

const LUT_SIZE = 256

/** Converts a hex color (#rgb / #rrggbb) to [r,g,b] in 0..255. Falls back to black. */
export function parseHex(hex: string): [number, number, number] {
  let h = hex.trim().replace(/^#/, '')
  if (h.length === 3) {
    h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]
  }
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return [0, 0, 0]
  const n = parseInt(h, 16)
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff]
}

/** Is this a valid hex color (#rgb or #rrggbb)? */
export function isHex(hex: string): boolean {
  return /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex.trim())
}

/** Normalizes to the lowercase #rrggbb form. */
export function normalizeHex(hex: string): string {
  const [r, g, b] = parseHex(hex)
  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')
}

/**
 * Builds/updates the palette LUT (256×1 RGBA) in a DataTexture.
 *
 * - `bands` = number of "color IDs": the gray range is split into N bands and
 *   each band takes a fixed color from the palette (discrete mapping, posterized look).
 * - `smooth` = continuous gradient: the gray is interpolated along the palette.
 *
 * If `target` is passed, its data is rewritten (without recreating the texture)
 * so the reference in the material isn't broken; otherwise a new one is created.
 */
export function buildPaletteLUT(
  palette: string[],
  bands: number,
  smooth: boolean,
  target?: THREE.DataTexture,
): THREE.DataTexture {
  const colors = (palette.length > 0 ? palette : ['#000000', '#ffffff']).map(parseHex)
  const len = colors.length
  const nBands = Math.max(1, Math.min(LUT_SIZE, Math.floor(bands)))

  const tex =
    target ??
    new THREE.DataTexture(new Uint8Array(LUT_SIZE * 4), LUT_SIZE, 1, THREE.RGBAFormat)
  const data = tex.image.data as Uint8Array

  for (let i = 0; i < LUT_SIZE; i++) {
    const g = i / (LUT_SIZE - 1)
    let r: number, gg: number, b: number
    if (smooth) {
      // Continuous position within the palette and linear interpolation.
      const p = g * (len - 1)
      const lo = Math.floor(p)
      const hi = Math.min(len - 1, lo + 1)
      const t = p - lo
      r = colors[lo][0] + (colors[hi][0] - colors[lo][0]) * t
      gg = colors[lo][1] + (colors[hi][1] - colors[lo][1]) * t
      b = colors[lo][2] + (colors[hi][2] - colors[lo][2]) * t
    } else {
      // Discrete band -> color ID -> palette color.
      const band = Math.min(nBands - 1, Math.floor(g * nBands))
      const idx = nBands > 1 ? Math.round((band / (nBands - 1)) * (len - 1)) : 0
      ;[r, gg, b] = colors[Math.min(len - 1, idx)]
    }
    const o = i * 4
    data[o] = r
    data[o + 1] = gg
    data[o + 2] = b
    data[o + 3] = 255
  }

  // NEAREST keeps the bands crisp; in smooth mode the LUT is already interpolated.
  tex.magFilter = smooth ? THREE.LinearFilter : THREE.NearestFilter
  tex.minFilter = tex.magFilter
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  tex.colorSpace = THREE.SRGBColorSpace
  tex.needsUpdate = true
  return tex
}

// ---------------------------------------------------------------------------
// Palette library in localStorage (no backend).
// ---------------------------------------------------------------------------

export interface SavedPalette {
  id: string
  name: string
  colors: string[]
}

const STORAGE_KEY = 'videolab.palettes'
const SEEDED_KEY = 'vs_seeded_palette_names'

/**
 * Built-in starter palettes (name + 2–5 hex colors). Seeded into the library once
 * each so users get a ready set; they remain fully editable/deletable afterwards.
 */
export const PRESET_PALETTES: { name: string; colors: string[] }[] = [
  // Pack 1
  { name: 'Fiery Ocean', colors: ['#7c0a0d', '#cf2230', '#f6ecd2', '#0e3a52', '#5d8aab'] },
  { name: 'Olive Garden Feast', colors: ['#5b6e2f', '#2e3b18', '#f7f0d2', '#cf9a5e', '#b7702f'] },
  { name: 'Pastel Dreamland Adventure', colors: ['#c2a2d4', '#f8c8da', '#f4a3c2', '#bfe0f5', '#a9cef0'] },
  { name: 'Refreshing Summer Fun', colors: ['#7cc0e0', '#1f8bb5', '#0a2f4a', '#f6b40e', '#f08008'] },
  { name: 'Sunny Beach Day', colors: ['#264653', '#2a9d8f', '#e9c46a', '#f4a261', '#e76f51'] },
  { name: 'Golden Summer Fields', colors: ['#b6c79a', '#dce6c2', '#f5f0cf', '#f0e2b6', '#d39b62'] },
  { name: 'Deep Sea', colors: ['#131c2a', '#1f2f49', '#44587c', '#8093b3', '#d9ded9'] },
  { name: 'Deep Blue Waters', colors: ['#2d6e9d', '#3080b0', '#ccd5d2', '#0e3a4d', '#7ac4dd'] },
  { name: 'Ocean Breeze', colors: ['#080857', '#0c6fb8', '#16a1da', '#9bd6ee', '#cdeaf6'] },
  { name: 'Fresh Greens', colors: ['#3e6a3e', '#6fa455', '#9dc75a', '#ebe1c9', '#bf4a4a'] },
  { name: 'Summer Ocean Breeze', colors: ['#e3494f', '#eef4ef', '#b6dde7', '#4a82a9', '#1e3a54'] },
  { name: 'Vibrant Color Fiesta', colors: ['#fbb913', '#f4540e', '#ef0f66', '#7c34d6', '#2f7cf0'] },
  { name: 'Soft Sand', colors: ['#ececea', '#cfc3b4', '#f0eadf', '#dfd3c1', '#ccc1b0'] },
  { name: 'Golden Twilight', colors: ['#05070f', '#0a2342', '#0b4884', '#f5c40e', '#f8d61e'] },
  { name: 'Mocha Latte', colors: ['#131312', '#29343a', '#f1f1ef', '#a18b79', '#4e463a'] },
  { name: 'Watermelon Sorbet', colors: ['#ef476f', '#ffd166', '#06d6a0', '#118ab2', '#073b4c'] },
  { name: 'Red Sunburst', colors: ['#4a0a18', '#7a0d2e', '#c8425a', '#f9774a', '#fa9d5c'] },
  { name: 'Earthy Tones', colors: ['#eeb1ba', '#f7e2d3', '#dcd8c8', '#aec3a4', '#485550'] },
  // Pack 3
  { name: 'Gothic Glam', colors: ['#080808', '#3c2440', '#8d2a6a', '#e34a72', '#ededf2'] },
  { name: 'Vintage Charm', colors: ['#102028', '#163029', '#e8d3a6', '#b8431a', '#3a2730'] },
  { name: 'Turquoise Harmony', colors: ['#0e7c96', '#129b8d', '#1bc89a', '#79dba2', '#eef0b7'] },
  { name: 'Colorful Daybreak', colors: ['#5b34c8', '#6c79f0', '#f9b417', '#f7951a', '#f3551a'] },
  { name: 'Earthy Forest Hues', colors: ['#d6dcc4', '#a9c08d', '#6f9c64', '#3f6b40', '#2d4a2e'] },
  { name: 'Rustic Charm', colors: ['#f8f4e2', '#cdc6b6', '#3a352e', '#211f1c', '#e8521f'] },
  { name: 'Coastal Vibes', colors: ['#3f648f', '#9dc4e1', '#e1f5f8', '#ec6a4f', '#28344a'] },
  { name: 'Rose Petal', colors: ['#8a0e2e', '#e23a5e', '#f06a92', '#f7b0c6', '#cdebf0'] },
  { name: 'Autumn Glow', colors: ['#7a0a26', '#f6b431', '#e8852a', '#e0622a', '#dc3a32'] },
  { name: 'Fiery Red', colors: ['#2a0e08', '#5a1808', '#8a1e0a', '#c83e0c', '#f6a81a'] },
  { name: 'Earthy Tones 2', colors: ['#e8e4c8', '#d6e2b4', '#8fb56a', '#a17f5f', '#5f5143'] },
  { name: 'Bold Hues', colors: ['#ec1a83', '#7d20c9', '#2e189c', '#3a6cf0', '#4db8ec'] },
  { name: 'Candy Pop', colors: ['#a650d8', '#f25ba8', '#f8e131', '#16b8ec', '#19d6a0'] },
  { name: 'Summer Sunset', colors: ['#ee9a5a', '#f4d3a0', '#88c8a9', '#1f9ab8', '#15375f'] },
  // Pack 4
  { name: 'Soft Serenity', colors: ['#d9c9f0', '#f9cfe0', '#f9d6c9', '#e3f2d6', '#d4eecb'] },
  { name: 'Raspberry Sorbet', colors: ['#e9b4a8', '#d96860', '#f9e3e3', '#9cce97', '#ef6f8e'] },
  { name: 'Sunset Hues', colors: ['#e4dcc6', '#c0a89b', '#94946a', '#5f6c58', '#3a5159'] },
  { name: 'Tropical Sunrise', colors: ['#f59518', '#fbbf6a', '#ffffff', '#d5f1ec', '#20bda6'] },
  { name: 'Sunny Spring Day', colors: ['#adb45a', '#8a4f72', '#eef3ec', '#b4a791', '#3e2f44'] },
  { name: 'Neutral Earth Tones', colors: ['#141210', '#1f3039', '#e6dccb', '#c1a988', '#4f4636'] },
  { name: 'Azure Skies', colors: ['#0a0a0a', '#3a72f0', '#e02a2a', '#f6c021', '#e2e4e2'] },
  { name: 'Gothic Romance', colors: ['#0a0808', '#3a121c', '#f0f0ee', '#a18b79', '#4e463a'] },
  { name: 'Cherry Blossom', colors: ['#ee5258', '#a82a44', '#16303a', '#f2f1ed', '#7a5a52'] },
  { name: 'Serene Blue', colors: ['#dde6ea', '#1f4e7a', '#5a8cb0', '#9dc8e6', '#8a8a8a'] },
  { name: 'Purple Haze', colors: ['#ddd0ee', '#c2aad6', '#9b5ec0', '#4a2e6e', '#1a2e44'] },
  { name: 'Blue Serenity', colors: ['#eaeefb', '#d9e2fb', '#c2d2f8', '#a8c0f5', '#7c9af0'] },
  { name: 'Pastel Dreamland', colors: ['#f6cdef', '#d3b0f0', '#b89af0', '#cfe6f8', '#cdf4f4'] },
  { name: 'Candy Floss Dreams', colors: ['#f9a092', '#f7f0d8', '#a8e6cc', '#a0d8d8'] },
  { name: 'Monochrome Beach', colors: ['#333333', '#3f7e86', '#ffffff', '#d2d2d2', '#2e567c'] },
  { name: 'Ocean Sunset', colors: ['#06182a', '#7e9a82', '#efd98a', '#d11414', '#8a0a0a'] },
  { name: 'Cozy Neutral Retreat', colors: ['#c89678', '#d8b49e', '#f5e2cf', '#9aa384', '#5e6b4e'] },
  { name: 'Refreshing Spring Hues', colors: ['#7df0e2', '#f9cfe0', '#f9e89a', '#b6f0a0', '#74e08e'] },
  { name: 'Candy Dream', colors: ['#4f93a8', '#9dc8e0', '#ec5e92', '#f7b0b8', '#f0c879'] },
  { name: 'Whimsical Harmony', colors: ['#aee6a8', '#c2eee8', '#bcd9f0', '#f4c8d2', '#ece2bc'] },
  { name: 'Summer Splash', colors: ['#ec4fd0', '#f06ab0', '#f99a8a', '#f9b48a', '#f9d45e'] },
  { name: 'Pastel Dreams', colors: ['#5a6ef0', '#7c8cf2', '#aac2f5', '#cfe2f8', '#d9f4f0'] },
  { name: 'Vibrant Summer', colors: ['#2e6eb0', '#3aa0d8', '#6bbcae', '#f6c00e', '#e8741a'] },
  { name: 'Sunset Shades', colors: ['#f7c89a', '#f7b48a', '#f49a82', '#f08a78', '#ee6a5a'] },
  { name: 'Purple Sunset', colors: ['#3a0a8a', '#8a0a5a', '#f0146a', '#f0500a', '#f6b40e'] },
  { name: 'Earthy Tones 3', colors: ['#9aa46e', '#c8cc8e', '#eef0d0', '#d0a89c', '#b08a8a'] },
  { name: 'Sunset Bliss', colors: ['#f6b440', '#e01a5e', '#8a1a4e', '#1f7e86', '#7cc8e0'] },
  { name: 'Golden Meadow', colors: ['#f0741a', '#f0d81a', '#7cc81e', '#4e8a0e', '#f0901a'] },
  { name: 'Midnight Serenade', colors: ['#1e2a4a', '#2a3e7a', '#5a6ea8', '#2a2620', '#f2f2f0'] },
  { name: 'Sunshine Beach Day', colors: ['#a86e0e', '#f6d68a', '#f6b42e', '#2e7ec8', '#2e96f0'] },
  { name: 'Deep Sea 2', colors: ['#0e1726', '#1e3450', '#44607e', '#8a9eb0', '#e6e2d2'] },
  { name: 'Cozy Fireside', colors: ['#221e1e', '#b14a30', '#8cc0cc', '#ecd89a', '#e6e0d2'] },
  { name: 'Cherry Blossom 2', colors: ['#f9d4dc', '#ec5e92', '#8a0a3e', '#2a3340', '#1a1f26'] },
  { name: 'Rainbow Burst', colors: ['#3ec81e', '#9cf04e', '#16c8c8', '#ee2a4e', '#f6961a'] },
  { name: 'Midnight Blues Mystery', colors: ['#4a7eb0', '#1f5ec8', '#4a4e54', '#1a1d22', '#080808'] },
  { name: 'Cool Coastal Vibes', colors: ['#2e2e44', '#8a96b4', '#e6edf0', '#ec2a3e', '#cc1424'] },
  { name: 'Soft Serenity 2', colors: ['#c2a096', '#e0c0b8', '#ecd8d2', '#f7f2f0', '#fbfbfb'] },
  { name: 'Cozy Comfort', colors: ['#b8a878', '#5e5460', '#8a5e5e', '#f2efe2', '#211f1c'] },
  { name: 'Forest Hues', colors: ['#1e3a34', '#1f7e7e', '#ecdcd4', '#c14a34', '#7a2e1e'] },
]

/** Adds any PRESET_PALETTES not seeded before (respects user deletions; never
 *  re-adds a preset once seeded). Returns the resulting palette list. */
export function seedPresetPalettes(): SavedPalette[] {
  let seeded: string[] = []
  try {
    seeded = JSON.parse(localStorage.getItem(SEEDED_KEY) || '[]')
  } catch {
    seeded = []
  }
  const seenSet = new Set(seeded)
  const existingNames = new Set(loadPalettes().map((p) => p.name.toLowerCase()))
  let changed = false
  // Insert in reverse so the first preset ends up on top (savePalette unshifts).
  for (const p of [...PRESET_PALETTES].reverse()) {
    if (seenSet.has(p.name)) continue
    seenSet.add(p.name)
    if (!existingNames.has(p.name.toLowerCase())) {
      savePalette(p.name, p.colors)
      changed = true
    }
  }
  if (changed || seenSet.size !== seeded.length) {
    try {
      localStorage.setItem(SEEDED_KEY, JSON.stringify([...seenSet]))
    } catch {
      /* ignore */
    }
  }
  return loadPalettes()
}

export function loadPalettes(): SavedPalette[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as SavedPalette[]) : []
  } catch {
    return []
  }
}

export function savePalette(name: string, colors: string[]): SavedPalette[] {
  const list = loadPalettes()
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  list.unshift({ id, name: name.trim() || 'Untitled', colors })
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
  } catch {
    /* quota full: ignore */
  }
  return list
}

export function deletePalette(id: string): SavedPalette[] {
  const list = loadPalettes().filter((p) => p.id !== id)
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
  } catch {
    /* ignore */
  }
  return list
}

// --- Contrast (WCAG) ------------------------------------------------------

/** WCAG relative luminance (0 = black … 1 = white). */
export function relativeLuminance(hex: string): number {
  const lin = parseHex(hex).map((v) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2]
}

/** WCAG contrast ratio between two colors (1 = none … 21 = black/white). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a)
  const lb = relativeLuminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/** Chroma / colorfulness (0 = gray … 1 = fully saturated). */
export function chroma(hex: string): number {
  const [r, g, b] = parseHex(hex)
  return (Math.max(r, g, b) - Math.min(r, g, b)) / 255
}

export interface PaletteAssignment {
  /** Background color stops (gradient when >1, else a solid). */
  background: string[]
  /** Primitive recolor palette (the remaining, more vivid colors). */
  primitive: string[]
  /** Lyrics text color — the palette color with the best contrast vs the background. */
  text: string
  /** Contrast ratio between `text` and the dominant background color. */
  textContrast: number
}

/**
 * Color-weight distribution across background / primitive / lyrics — a fast,
 * deterministic (no-AI) assignment based on each color's perceptual weight:
 *  - background = the "calmest" colors (dark and/or low chroma) since it's a large,
 *    non-focal area that shouldn't fight the content;
 *  - lyrics text = the color with the strongest contrast vs the background (legible);
 *  - primitive = the most vivid (high-chroma) remaining colors, since it's the
 *    animated focal element that should pop.
 * Uneven splits are fine — fit matters more than equal counts.
 */
export function distributePalette(colors: string[]): PaletteAssignment {
  const list = colors.length ? colors : ['#000000']
  const meta = list.map((c) => ({ c, lum: relativeLuminance(c), chr: chroma(c) }))
  // Background fit: dark and muted score highest (calm, recedes).
  const bgFit = (m: { lum: number; chr: number }) => (1 - m.lum) * 0.55 + (1 - m.chr) * 0.45
  const byBg = [...meta].sort((a, b) => bgFit(b) - bgFit(a))
  const background = list.length >= 4 ? [byBg[0].c, byBg[1].c] : [byBg[0].c]
  const bgDominant = byBg[0].c
  // Lyrics text: maximize contrast vs the background.
  let text = byBg[0].c
  let best = -1
  for (const m of meta) {
    const cr = contrastRatio(m.c, bgDominant)
    if (cr > best) {
      best = cr
      text = m.c
    }
  }
  // Primitive: remaining colors, most vivid first (highest chroma).
  const used = new Set(background)
  let primitive = meta
    .filter((m) => !used.has(m.c))
    .sort((a, b) => b.chr - a.chr)
    .map((m) => m.c)
  if (primitive.length === 0) primitive = [...list]
  return { background, primitive, text, textContrast: best }
}

/** Updates a palette's name and/or colors (kept between 2 and 5 colors). */
export function updatePalette(id: string, partial: Partial<Omit<SavedPalette, 'id'>>): SavedPalette[] {
  const list = loadPalettes().map((p) =>
    p.id === id
      ? {
          ...p,
          ...(partial.name !== undefined ? { name: partial.name.trim() || 'Untitled' } : {}),
          ...(partial.colors ? { colors: partial.colors.slice(0, 5) } : {}),
        }
      : p,
  )
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
  } catch {
    /* ignore */
  }
  return list
}
